import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { getUserRole } from '../utils/rolePermissions';
import { onboardingSteps } from '../config/onboardingSteps';
import OnboardingSpotlight from './OnboardingSpotlight';
import OnboardingTooltip from './OnboardingTooltip';
import '../styles/OnboardingWizard.css';

const OnboardingWizard = ({
  isOpen,
  onClose,
  onComplete,
  onSkip,
  user,
  overrideRole = null,
  onSectionChange = null,
  currentSection = null
}) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [targetElement, setTargetElement] = useState(null);
  const [elementRect, setElementRect] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [navigationInProgress, setNavigationInProgress] = useState(false);

  const userRole = overrideRole || getUserRole(user);
  const steps = React.useMemo(() => onboardingSteps[userRole] || [], [userRole]);

  // Reset step index when wizard opens or role changes
  useEffect(() => {
    if (isOpen) {
      setCurrentStepIndex(0);
    }
  }, [isOpen, userRole]);

  const findTargetElement = useCallback((step) => {
    const selectors = step.selector.split(',').map(s => s.trim());
    let element = null;

    for (const selector of selectors) {
      element = document.querySelector(selector);
      if (element) {
        break;
      }
    }

    if (element) {
      if (step.expandAccordion) {
        const isAccordionOpen = element.classList.contains('accordion-open');
        if (!isAccordionOpen) {
          element.click();
        }
      }

      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });

      setTimeout(() => {
        let targetEl = element;

        if (step.expandAccordion && element.classList.contains('accordion-open')) {
          const accordionContainer = element.closest('.nav-accordion-container');
          if (accordionContainer) {
            targetEl = accordionContainer;
          }
        }

        const rect = targetEl.getBoundingClientRect();
        setTargetElement(targetEl);
        setElementRect(rect);
        setIsCalculating(false);
        setNavigationInProgress(false);
      }, 200);
    } else {

      let retryCount = 0;
      const maxRetries = 15;
      const retryInterval = 400;

      const retryFind = () => {
        let retryElement = null;
        for (const selector of selectors) {
          retryElement = document.querySelector(selector);
          if (retryElement) break;
        }

        if (retryElement) {
          if (step.expandAccordion) {
            const isAccordionOpen = retryElement.classList.contains('accordion-open');
            if (!isAccordionOpen) {
              retryElement.click();
            }
          }

          retryElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
          setTimeout(() => {
            let targetEl = retryElement;

            if (step.expandAccordion && retryElement.classList.contains('accordion-open')) {
              const accordionContainer = retryElement.closest('.nav-accordion-container');
              if (accordionContainer) {
                targetEl = accordionContainer;
              }
            }

            const rect = targetEl.getBoundingClientRect();
            setTargetElement(targetEl);
            setElementRect(rect);
            setIsCalculating(false);
            setNavigationInProgress(false);
          }, 200);
        } else if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(retryFind, retryInterval);
        } else {
          setTargetElement(null);
          setElementRect(null);
          setIsCalculating(false);
          setNavigationInProgress(false);
        }
      };

      setTimeout(retryFind, retryInterval);
    }
  }, []);

  const findAndHighlightElement = useCallback(() => {
    if (!steps[currentStepIndex]) return;

    const step = steps[currentStepIndex];

    if (step.requiredSection && onSectionChange && currentSection !== step.requiredSection) {
      setNavigationInProgress(true);
      onSectionChange(step.requiredSection);
      return;
    }

    if (step.parentAccordion && !step.expandAccordion) {
      const parentSelectors = step.parentAccordion.split(',').map(s => s.trim());
      let parentElement = null;

      for (const selector of parentSelectors) {
        parentElement = document.querySelector(selector);
        if (parentElement) break;
      }

      if (parentElement) {
        const isAccordionOpen = parentElement.classList.contains('accordion-open');
        if (!isAccordionOpen) {
          parentElement.click();
          setTimeout(() => {
            findTargetElement(step);
          }, 300);
          return;
        }
      }
    }

    findTargetElement(step);
  }, [currentStepIndex, steps, onSectionChange, currentSection, findTargetElement]);

  useEffect(() => {
    if (isOpen) {
      setIsCalculating(true);
      setTimeout(() => {
        findAndHighlightElement();
      }, 50);
    }
  }, [isOpen, currentStepIndex, findAndHighlightElement]);

  useEffect(() => {
    if (navigationInProgress && currentSection) {
      setTimeout(() => {
        findAndHighlightElement();
      }, 50);
    }
  }, [currentSection, navigationInProgress, findAndHighlightElement]);

  const handleComplete = useCallback(() => {
    if (onSectionChange) {
      onSectionChange('dashboard');
    }
    if (onComplete) {
      onComplete();
    }
    onClose();
  }, [onComplete, onClose, onSectionChange]);

  const handleNext = useCallback(() => {
    if (currentStepIndex < steps.length - 1) {
      setIsCalculating(true);
      setCurrentStepIndex(currentStepIndex + 1);
    } else {
      handleComplete();
    }
  }, [currentStepIndex, steps.length, handleComplete]);

  const handlePrevious = useCallback(() => {
    if (currentStepIndex > 0) {
      setIsCalculating(true);
      setCurrentStepIndex(currentStepIndex - 1);
    }
  }, [currentStepIndex, steps]);

  const handleSkip = useCallback(() => {
    if (onSectionChange) {
      onSectionChange('dashboard');
    }
    if (onSkip) {
      onSkip();
    }
    onClose();
  }, [onSkip, onClose, onSectionChange]);

  const handleExit = useCallback(() => {
    if (onSectionChange) {
      onSectionChange('dashboard');
    }
    if (onSkip) {
      onSkip();
    }
    onClose();
  }, [onSkip, onClose, onSectionChange]);

  useEffect(() => {
    if (!isOpen) return;

    const handleResize = () => {
      if (targetElement) {
        const rect = targetElement.getBoundingClientRect();
        setElementRect(rect);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleExit();
      } else if (e.key === 'ArrowRight' && currentStepIndex < steps.length - 1) {
        handleNext();
      } else if (e.key === 'ArrowLeft' && currentStepIndex > 0) {
        handlePrevious();
      }
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, targetElement, currentStepIndex, steps.length, handleNext, handlePrevious, handleExit]);

  if (!overrideRole && userRole === 'super_admin') {
    return null;
  }

  if (!isOpen || steps.length === 0) {
    return null;
  }

  const currentStep = steps[currentStepIndex];

  // Safety check: if currentStep is undefined (race condition during role switch), don't render
  if (!currentStep) {
    return null;
  }

  return ReactDOM.createPortal(
    <>
      <OnboardingSpotlight
        elementRect={elementRect}
        highlightPadding={currentStep?.highlightPadding || 8}
        disableLeftBackdrop={currentStep?.disableLeftBackdrop}
        noLeftOffset={currentStep?.noLeftOffset}
      />
      {!isCalculating && elementRect && (
        <OnboardingTooltip
          content={{
            title: currentStep.title,
            description: currentStep.description
          }}
          position={currentStep.position || 'auto'}
          currentStep={currentStepIndex}
          totalSteps={steps.length}
          onNext={handleNext}
          onPrevious={handlePrevious}
          onSkip={handleSkip}
          onExit={handleExit}
          elementRect={elementRect}
          isFirstStep={currentStepIndex === 0}
          isLastStep={currentStepIndex === steps.length - 1}
        />
      )}
    </>,
    document.body
  );
};

export default OnboardingWizard;
